import styled, { css } from "styled-components";
import type { RowData } from "@tanstack/react-table";
import type { DataTableRow as TableRow } from "./DataTable";

type DataTableRowProps<T extends RowData> = {
  readonly children: React.ReactNode;
  readonly row: TableRow<T>;
  readonly onClick?: () => void;
  readonly isRowInteractive?: (row: TableRow<T>) => boolean;
  readonly style?: React.CSSProperties;
  readonly onMouseEnter?: () => void;
  readonly onMouseLeave?: () => void;
};

export function DataTableRow<T extends RowData>({
  children,
  row,
  onClick,
  isRowInteractive,
  style,
  onMouseEnter,
  onMouseLeave,
}: DataTableRowProps<T>) {
  const isInteractive = isRowInteractive ? isRowInteractive(row) : false;
  const rowTestId = `data-table-row-${row.id}`;
  return isInteractive ? (
    <InteractiveRow
      data-testid={rowTestId}
      onClick={onClick}
      style={style}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {children}
    </InteractiveRow>
  ) : (
    <tr
      data-testid={rowTestId}
      style={style}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {children}
    </tr>
  );
}

const cssActive = css`
  td {
    background-color: ${({ theme }) => theme.colors.backgroundPressed};
  }

  svg {
    color: ${({ theme }) => theme.colors.contentInteractive};
  }
`;

const InteractiveRow = styled.tr`
  cursor: default;

  &:hover {
    td {
      background-color: ${({ theme }) => theme.colors.backgroundHover};
    }

    svg {
      color: ${({ theme }) => theme.colors.contentInteractive};
    }
  }

  &:active {
    ${cssActive}
  }

  &:focus-within {
    ${cssActive}
  }
`;
